/**
 * AttachmentsBlock — the work-item drawer's Attachments section (0162 /
 * ADR-0048; design of record: build-specs/card-attachments-design.md
 * §6.1, §6.4). Working files that belong WITH a card: a vendor brochure
 * on a resources card, a resume on a hiring card.
 *
 * It speaks the redesigned drawer's anatomy (drawerLayout, #621): a
 * DrawerSection card on the paper surface, collapsible, its `meta`
 * carrying the count so a folded section never hides that it holds
 * something. It is NOT the pre-redesign hand-rolled headline.
 *
 * THE BOARD FENCE (design §1.4, JC-2 — not cuttable). The section does
 * not render AT ALL unless the card's process has attachments_enabled.
 * The intake board is seeded OFF, because its cards name prospective
 * clients. The check here is fail-CLOSED (only an explicit true opens
 * the surface) and it is a DISPLAY fence: the server re-checks the flag
 * on every write, so this can never be the only thing standing between
 * a fenced board and an upload.
 *
 * WHAT DOES NOT BELONG HERE — AND WHY THE TWO STORES STAY SEPARATE.
 * Client documents (POLSTs, POAs, insurance cards, consents, the Client
 * Setup paperwork) live in `client_documents`: encrypted at rest,
 * care-team fenced, every read written to phi_access_log. Card files
 * live in `work_item_attachments`, which is deliberately TENANT-tier —
 * plaintext filename, no care-team RLS, no PHI-access emission — because
 * a vendor brochure or a candidate's resume is not clinical content.
 *
 * The intake drawer therefore carries a SEPARATE section
 * (ClientDocumentsBlock) that reads and writes the client's real
 * `client_documents` rows once Client Setup has created the client. A
 * future contributor may be tempted to "unify" the two surfaces into one
 * store: DO NOT. Pointing intake paperwork at this tenant-tier table is
 * precisely what the 0162 fence below exists to prevent, and pushing
 * card files into the clinical store would put brochures behind a
 * care-team fence and log them as PHI reads. Same drawer, two registers,
 * on purpose.
 *
 * OPEN vs DOWNLOAD (§6.4, founder ruling 6). One primary action per
 * row, chosen by the STORED content type: pdf/png/jpeg get Open (an
 * inline grant, rendered in a new tab); docx gets Download. The client
 * only picks the LABEL — the server coerces the disposition against the
 * stored type, so a hand-crafted inline request for a docx still comes
 * back as a download. The popup dance (open a blank tab synchronously
 * on the click, sever its opener by hand, point it at the grant when it
 * lands) is the shipped house-safe pattern from DocumentsTab, not a
 * re-derivation: passing 'noopener' to window.open returns null and
 * takes the handle away, which is the non-obvious part.
 *
 * DELETE IS PERMANENT (§3, JC-4 ratified). No archive shelf, no undo.
 * The row action is a quiet DestructiveTextAction and the confirm
 * carries the weight; its copy must never imply the file can come back.
 * The 30-day blob soft-delete on the storage account is an operator
 * assisted recovery that does NOT restore the register row, so it is
 * not an undo and is not mentioned to the user as one.
 */

import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  isAttachmentPreviewable,
  resolveAttachmentContentType,
  workItemAttachmentsApi,
  type AttachmentsListResponse,
  type WorkItemAttachmentWire,
} from '@/lib/workItemAttachments';
import { workItemAttachmentsKey } from '@/lib/workspace';
import { openBlankViewerTab, pointViewerAt, saveGrantToDisk } from '@/lib/documentGrants';
import { Button } from '@/components/primitives/Button';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { TextAction, DestructiveTextAction } from '@/components/workspace/TextAction';
import { DrawerSection } from './drawerLayout';
import { shortDate } from '@/components/workspace/workspaceFormat';
import { WORKSPACE_DRAWER_ATTACHMENTS_KEY } from '@/lib/keptAcrossSignOut';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/** The upload's retry line; `{contact_system}` is filled in at render. */
const UPLOAD_FAILED_COPY = "That file didn't upload. Add it again, and if it fails again, tell {contact_system}.";

/** The open or download failure line (house failure copy). */
const GRANT_FAILED_COPY =
  "That file didn't open. Try again, and if it happens again, tell {contact_system}.";

/** What the picker will not take, said in file terms and not mime terms. */
const TYPE_REJECTED_COPY =
  'That file type is not one this card takes. A PDF, Word document, or image will go right in.';

const SIZE_REJECTED_COPY = 'That file is over the 25 MB limit for a card file.';

/** Quiet size label (a fact for the meta line). */
export function formatAttachmentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export interface AttachmentsBlockProps {
  tenantSlug: string;
  workItemId: string;
  /**
   * The card's process fence (design §1.4). Fail-closed: anything other
   * than an explicit true renders nothing at all.
   */
  attachmentsEnabled: boolean | undefined;
  /** manage_work_items — upload + delete. The server re-checks. */
  canManage: boolean;
  /** Set once an intake has converted; links the empty state to the record. */
  clientId: string | null;
}

export function AttachmentsBlock({
  tenantSlug,
  workItemId,
  attachmentsEnabled,
  canManage,
  clientId,
}: AttachmentsBlockProps): ReactNode {
  const contact = useSupportContact({ enabled: false });
  const enabled = attachmentsEnabled === true;
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** The last attempted file, retained so retry is one click. */
  const [lastAttempt, setLastAttempt] = useState<File | null>(null);
  /** A client-side rejection, surfaced BEFORE any byte leaves the browser. */
  const [pickError, setPickError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<WorkItemAttachmentWire | null>(null);

  const queryKey = workItemAttachmentsKey(tenantSlug, workItemId);
  const listQuery = useQuery<AttachmentsListResponse>({
    queryKey,
    queryFn: ({ signal }) => workItemAttachmentsApi.list(tenantSlug, workItemId, signal),
    enabled,
  });
  const rows = listQuery.data?.rows ?? [];

  /** Both the section list AND the board (the card face count) refresh. */
  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: ['work-items', tenantSlug] });
  }

  const uploadMut = useMutation({
    mutationFn: (file: File) =>
      workItemAttachmentsApi.uploadAttachment(tenantSlug, workItemId, file),
    onSuccess: () => {
      setLastAttempt(null);
      invalidate();
    },
  });

  const removeMut = useMutation({
    mutationFn: (attachmentId: string) =>
      workItemAttachmentsApi.deleteAttachment(tenantSlug, attachmentId),
    onSuccess: () => {
      setRemoveTarget(null);
      invalidate();
    },
  });

  const downloadMut = useMutation({
    mutationFn: (attachmentId: string) =>
      workItemAttachmentsApi.downloadGrant(tenantSlug, attachmentId, 'attachment'),
    onSuccess: (grant) => {
      saveGrantToDisk(grant.download_url);
    },
  });

  const openMut = useMutation({
    mutationFn: (vars: { attachmentId: string; viewer: Window | null }) =>
      workItemAttachmentsApi.downloadGrant(tenantSlug, vars.attachmentId, 'inline'),
    onSuccess: (grant, vars) => {
      pointViewerAt(vars.viewer, grant.download_url);
    },
    onError: (_err, vars) => {
      // Never strand a blank tab behind a failed grant.
      vars.viewer?.close();
    },
  });

  function openAttachment(attachmentId: string): void {
    // Synchronous open on the gesture, pointed at the grant when it
    // lands (see documentGrants.ts).
    openMut.mutate({ attachmentId, viewer: openBlankViewerTab() });
  }

  /** Client-side policy first: the user hears no before anything uploads. */
  function startUpload(file: File): void {
    setPickError(null);
    if (resolveAttachmentContentType(file) === null) {
      setPickError(TYPE_REJECTED_COPY);
      return;
    }
    if (file.size <= 0 || file.size > ATTACHMENT_MAX_BYTES) {
      setPickError(SIZE_REJECTED_COPY);
      return;
    }
    setLastAttempt(file);
    uploadMut.mutate(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (!canManage || uploadMut.isPending) return;
    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  }

  // The fence: nothing renders on a board whose process has the
  // attachment surface turned off.
  if (!enabled) return null;

  const showUploadError = !uploadMut.isPending && (uploadMut.isError || pickError !== null);

  return (
    <>
      <DrawerSection
        title="Attachments"
        testId="drawer-attachments"
        collapsible
        defaultOpen={false}
        storageKey={WORKSPACE_DRAWER_ATTACHMENTS_KEY}
        meta={rows.length > 0 ? (rows.length === 1 ? '1 file' : `${rows.length} files`) : undefined}
      >
        {canManage && (
          <div
            data-testid="attachments-upload-zone"
            className="rounded-card border border-dashed border-sage-300 bg-cream/50 px-4 py-3 text-center mb-3"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <input
              ref={fileInputRef}
              data-testid="attachments-upload-input"
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) startUpload(file);
              }}
            />
            {uploadMut.isPending ? (
              <p data-testid="attachments-uploading" className="text-[12.5px] text-ink" role="status">
                Uploading…
              </p>
            ) : (
              <>
                <p className="text-[12.5px] text-ink-soft mb-2">Drag a file here, or</p>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="attachments-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Add a file
                </Button>
              </>
            )}
            {showUploadError && (
              <p role="alert" data-testid="attachments-upload-error" className="text-[11.5px] text-pepper-deep mt-2">
                {pickError ?? contactSentence(UPLOAD_FAILED_COPY, contact)}{' '}
                {pickError === null && lastAttempt && (
                  <TextAction
                    data-testid="attachments-upload-retry"
                    onClick={() => uploadMut.mutate(lastAttempt)}
                  >
                    Try again
                  </TextAction>
                )}
              </p>
            )}
          </div>
        )}

        {listQuery.isError ? (
          <p role="alert" data-testid="attachments-list-error" className="text-[12.5px] text-pepper-deep">
            The files for this card did not load.{' '}
            <TextAction onClick={() => void listQuery.refetch()}>Try again</TextAction>
          </p>
        ) : rows.length === 0 ? (
          <div data-testid="attachments-empty" className="text-[12.5px] text-ink-soft">
            <p>Add the working files for this card.</p>
            <p className="mt-1">
              Client documents, POLSTs, POAs, insurance cards and consents belong on the
              client record&rsquo;s Documents tab.
              {clientId !== null && (
                <>
                  {' '}
                  <a
                    href={`/clients/${encodeURIComponent(clientId)}?tab=documents`}
                    data-testid="attachments-client-documents-link"
                    className="text-sage-800 underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
                  >
                    Open this client&rsquo;s Documents tab
                  </a>
                </>
              )}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-sage-800/[6%]" data-testid="attachments-list">
            {rows.map((row) => {
              const previewable = isAttachmentPreviewable(row.content_type);
              return (
                <li
                  key={row.id}
                  data-testid={`attachment-row-${row.id}`}
                  className="flex flex-col gap-0.5 py-2"
                >
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <span className="text-[12.5px] font-medium text-ink break-all min-w-0 flex-1">
                      {row.display_name}
                    </span>
                    {previewable ? (
                      <TextAction
                        data-testid={`attachment-open-${row.id}`}
                        disabled={openMut.isPending && openMut.variables?.attachmentId === row.id}
                        onClick={() => openAttachment(row.id)}
                      >
                        Open
                      </TextAction>
                    ) : (
                      <TextAction
                        data-testid={`attachment-download-${row.id}`}
                        disabled={downloadMut.isPending && downloadMut.variables === row.id}
                        onClick={() => downloadMut.mutate(row.id)}
                      >
                        Download
                      </TextAction>
                    )}
                    {canManage && (
                      <DestructiveTextAction
                        data-testid={`attachment-delete-${row.id}`}
                        onClick={() => setRemoveTarget(row)}
                      >
                        Delete
                      </DestructiveTextAction>
                    )}
                  </div>
                  <p className="text-[11.5px] text-sage-400">
                    {row.uploaded_by_display_name ? `${row.uploaded_by_display_name} · ` : ''}
                    {shortDate(row.created_at)} · {formatAttachmentSize(row.size_bytes)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}

        {(downloadMut.isError || openMut.isError) && (
          <p role="alert" data-testid="attachments-grant-error" className="text-[11.5px] text-pepper-deep mt-2">
            {contactSentence(GRANT_FAILED_COPY, contact)}
          </p>
        )}
      </DrawerSection>

      {/* Permanent, and the copy says so plainly. There is no archive
          shelf on a card and no in-product way back. */}
      {removeTarget !== null && (
        <ConfirmDialog
          title="Delete this file?"
          body={`"${removeTarget.display_name}" leaves this card for everyone, and the file itself is deleted. This can't be undone.`}
          confirmLabel="Delete"
          destructive
          onCancel={() => setRemoveTarget(null)}
          onConfirm={() => removeMut.mutate(removeTarget.id)}
        />
      )}
    </>
  );
}
